/**
 * Data Contract Loader Utility
 * 
 * This utility provides a centralized way to load all data from the data-contract folder.
 * All frontend data should come from data-contract, not be hardcoded in components.
 * 
 * This ensures:
 * 1. Frontend uses same data as backend will use (from Hasura API)
 * 2. Aurora RDS will be created with same schema as data-contract
 * 3. Single source of truth for all data definitions
 * 
 * Pattern:
 * - Each domain in data-contract/domain/json-examples/ contains normalized JSON files
 * - This utility imports and consolidates that data into usable formats
 * - Components import data from this utility, not from constants
 */

// ============================================================================
// KNOWLEDGEBASE DOMAIN - Food Classifications, Protocols, Escalation
// ============================================================================

import foodClassificationsJson from '../mocks/data-contract/knowledgebase/json-examples/food_classifications.json';
import protocolTemplatesJson from '../mocks/data-contract/knowledgebase/json-examples/protocol_templates.json';
import protocolMealGuidelinesJson from '../mocks/data-contract/knowledgebase/json-examples/protocol_template_meal_guidelines.json';
import protocolTimingRulesJson from '../mocks/data-contract/knowledgebase/json-examples/protocol_template_timing_rules.json';
import followUpSchedulesJson from '../mocks/data-contract/knowledgebase/json-examples/follow_up_schedules.json';
import escalationStrategiesJson from '../mocks/data-contract/knowledgebase/json-examples/escalation_strategies.json';
import escalationActionsJson from '../mocks/data-contract/knowledgebase/json-examples/escalation_strategy_actions.json';

export type FoodClassification = {
  id: string;
  category: string;
  food: string;
  classification: 'GREEN' | 'YELLOW' | 'RED';
  portion: string;
  notes: string;
};

type ProtocolTemplateRecord = {
  id: string;
  name: string;
  description: string;
  target_population: string;
  target_fasting: string;
  target_post_meal: string;
  target_bedtime: string;
};

type ProtocolMealGuidelineRecord = {
  id: string;
  template_id: string;
  sequence: number;
  guideline: string;
};

type ProtocolTimingRuleRecord = {
  id: string;
  template_id: string;
  sequence: number;
  rule: string;
};

export type FollowUpScheduleRecord = {
  id: string;
  phase: string;
  weeks: string;
  visits: string;
  focus: string;
  deliverables: string;
};

type EscalationUrgency = 'EMERGENCY' | 'URGENT' | 'HIGH' | 'MODERATE';

type EscalationStrategyRecord = {
  id: string;
  trigger: string;
  criteria: string;
  urgency: EscalationUrgency;
};

type EscalationActionRecord = {
  id: string;
  strategy_id: string;
  sequence: number;
  action: string;
};

export type ProtocolTemplateWithDetails = {
  id: string;
  name: string;
  description: string;
  targetPopulation: string;
  glucoseTargets: {
    fasting: string;
    postMeal: string;
    bedtime: string;
  };
  mealGuidelines: string[];
  timingRules: string[];
};

export type EscalationStrategyWithActions = {
  id: string;
  trigger: string;
  criteria: string;
  urgency: EscalationUrgency;
  actions: string[];
};

/**
 * Load food classifications from knowledgebase contract
 * 
 * @returns Array of food classifications with GREEN/YELLOW/RED stoplight categorization
 */
export const loadFoodClassifications = (): FoodClassification[] => foodClassificationsJson as FoodClassification[];

/**
 * Load protocol templates from knowledgebase contract
 * 
 * @returns Array of standard protocol templates (Type 2 Diabetes, Prediabetes, etc.)
 */
export const loadProtocolTemplates = (): ProtocolTemplateRecord[] => protocolTemplatesJson as ProtocolTemplateRecord[];

/**
 * Load meal guidelines for protocol templates
 * 
 * Normalized table: one row per guideline, references template_id
 * 
 * @returns Array of meal guideline items linked to protocol templates
 */
export const loadProtocolMealGuidelines = (): ProtocolMealGuidelineRecord[] =>
  protocolMealGuidelinesJson as ProtocolMealGuidelineRecord[];

/**
 * Load timing rules for protocol templates
 * 
 * Normalized table: one row per rule, references template_id
 * 
 * @returns Array of timing rule items linked to protocol templates
 */
export const loadProtocolTimingRules = (): ProtocolTimingRuleRecord[] =>
  protocolTimingRulesJson as ProtocolTimingRuleRecord[];

/**
 * Reconstruct protocol template with guidelines and timing rules
 * 
 * Normalizes the database view back into nested object for frontend display
 * 
 * @returns Array of protocol templates with mealGuidelines and timingRules nested
 */
export const loadProtocolTemplatesWithDetails = (): ProtocolTemplateWithDetails[] => {
  const templates = loadProtocolTemplates();
  const guidelines = loadProtocolMealGuidelines();
  const timingRules = loadProtocolTimingRules();

  return templates.map((template) => ({
    id: template.id,
    name: template.name,
    description: template.description,
    targetPopulation: template.target_population,
    glucoseTargets: {
      fasting: template.target_fasting,
      postMeal: template.target_post_meal,
      bedtime: template.target_bedtime,
    },
    mealGuidelines: guidelines
      .filter((g) => g.template_id === template.id)
      .sort((a, b) => a.sequence - b.sequence)
      .map((g) => g.guideline),
    timingRules: timingRules
      .filter((t) => t.template_id === template.id)
      .sort((a, b) => a.sequence - b.sequence)
      .map((t) => t.rule),
  }));
};

/**
 * Load follow-up schedule phases
 * 
 * @returns Array of follow-up schedule phases with visit frequency and focus
 */
export const loadFollowUpSchedules = (): FollowUpScheduleRecord[] =>
  followUpSchedulesJson as FollowUpScheduleRecord[];

/**
 * Load escalation strategies
 * 
 * Normalized table: one row per strategy, action items in separate table
 * 
 * @returns Array of escalation strategy triggers with urgency levels
 */
export const loadEscalationStrategies = (): EscalationStrategyRecord[] =>
  escalationStrategiesJson as EscalationStrategyRecord[];

/**
 * Load escalation action items
 * 
 * Normalized table: one row per action, references strategy_id
 * 
 * @returns Array of action items linked to escalation strategies
 */
export const loadEscalationActions = (): EscalationActionRecord[] =>
  escalationActionsJson as EscalationActionRecord[];

/**
 * Reconstruct escalation strategies with actions
 * 
 * Normalizes the database view back into nested object for frontend display
 * 
 * @returns Array of escalation strategies with actions nested
 */
export const loadEscalationStrategiesWithActions = (): EscalationStrategyWithActions[] => {
  const strategies = loadEscalationStrategies();
  const actions = loadEscalationActions();

  return strategies.map((strategy) => ({
    ...strategy,
    actions: actions
      .filter((a) => a.strategy_id === strategy.id)
      .sort((a, b) => a.sequence - b.sequence)
      .map((a) => a.action),
  }));
};

// ============================================================================
// HEALTH DATA LOADER PATTERN (for future domains)
// ============================================================================

/**
 * Generic loader pattern for future domains
 * 
 * As you add more domains (user, meal, cgm, protocol, etc.), follow this pattern:
 * 
 * 1. Create data-contract/domain/json-examples/entity.json
 * 2. Import it here: import entityJson from '../mocks/data-contract/domain/json-examples/entity.json'
 * 3. Export a loader function: export const loadEntity = () => entityJson
 * 4. In components, use: const data = loadEntity()
 * 
 * This ensures:
 * - Data is defined once (in data-contract)
 * - Components are minimal and focused on UI
 * - Backend teams can implement from same data
 * - RDS schema will match data structure exactly
 */

// Future: User domain
// import usersJson from '../mocks/data-contract/user/json-examples/user.json';
// export const loadUsers = () => usersJson;

// Future: Meal domain
// import mealEntriesJson from '../mocks/data-contract/meal/json-examples/meal-entry.json';
// export const loadMealEntries = () => mealEntriesJson;

// Future: CGM domain
// import cgmEventsJson from '../mocks/data-contract/cgm/json-examples/cgm-event.json';
// export const loadCgmEvents = () => cgmEventsJson;

// ============================================================================
// OPTIONAL: Caching Layer (if data is loaded frequently)
// ============================================================================

/**
 * Cache for loaded data (optional, for performance)
 * Remove if data is small enough that caching isn't necessary
 */
const dataCache = new Map<string, unknown>();

/**
 * Cache-aware loader pattern
 * 
 * Usage: const data = getCachedData('foodClassifications', loadFoodClassifications);
 */
export const getCachedData = <T>(cacheKey: string, loaderFn: () => T): T => {
  if (!dataCache.has(cacheKey)) {
    dataCache.set(cacheKey, loaderFn());
  }
  return dataCache.get(cacheKey) as T;
};

// ============================================================================
// EXPORT: Complete Data Context
// ============================================================================

/**
 * Load all adherence-related data
 * Useful for initializing page state in one call
 */
export const loadAllAdherenceData = () => ({
  foodClassifications: loadFoodClassifications(),
  protocolTemplates: loadProtocolTemplatesWithDetails(),
  followUpSchedules: loadFollowUpSchedules(),
  escalationStrategies: loadEscalationStrategiesWithActions(),
});

/**
 * Load all data without nesting (normalized database view)
 * Useful for components that work with normalized data directly
 */
export const loadAllAdherenceDataNormalized = () => ({
  foodClassifications: loadFoodClassifications(),
  protocolTemplates: loadProtocolTemplates(),
  protocolMealGuidelines: loadProtocolMealGuidelines(),
  protocolTimingRules: loadProtocolTimingRules(),
  followUpSchedules: loadFollowUpSchedules(),
  escalationStrategies: loadEscalationStrategies(),
  escalationActions: loadEscalationActions(),
});
